import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';

const ACCIONES = ['', 'Agrega', 'Modifica', 'Elimina'];

function nombreModulo(path) {
    if (!path) return 'Sistema';
    if (path.endsWith('/dashboard-stats') || path.includes('/dashboard')) return 'Dashboard';
    if (path.includes('/vehiculos')) return 'Vehículos';
    if (path.includes('/mantenciones')) return 'Mantenciones';
    if (path.includes('/contratistas') || path.includes('/sucursales')) return 'Contratistas';
    if (path.includes('/personal')) return 'Personal';
    if (path.includes('/documentos') || path.includes('/tipos-documento')) return 'Documentos';
    if (path.includes('/licencias')) return 'Licencias Médicas';
    if (path.includes('/chat') || path.includes('/mensajes')) return 'Mensajes';
    if (path.includes('/cargos')) return 'Cargos';
    if (path.includes('/pautas')) return 'Pautas Mantención';
    if (path.includes('/entrega') || path.includes('/articulos') || path.includes('/categorias-seguridad')) return 'Artículos de Seguridad';
    if (path.includes('/empresas') || path.includes('/usuarios')) return 'Administración';
    if (path.includes('/auth')) return 'Autenticación';
    return 'Otro';
}

function formatFecha(fecha) {
    if (!fecha) return '---';
    return new Date(fecha).toLocaleString('es-CL');
}

function jsonViewer(data) {
    if (data === null || data === undefined) return <em style={{ color: 'var(--text-muted)' }}>Sin datos</em>;
    let parsed = data;
    if (typeof data === 'string') {
        try { parsed = JSON.parse(data); } catch (e) { /* mantener string */ }
    }
    return (
        <pre style={{ background: '#f1f5f9', padding: '12px', borderRadius: '6px', overflow: 'auto', maxHeight: '260px', fontSize: '0.8rem' }}>
            {JSON.stringify(parsed, null, 2)}
        </pre>
    );
}

function isEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
}

function formatValue(v, max = 120) {
    if (v === null || v === undefined) return '—';
    let text;
    if (typeof v === 'object') text = JSON.stringify(v);
    else text = String(v);
    if (text.length > max) return text.substring(0, max) + '…';
    return text;
}

function renderDiff(anterior, nuevo) {
    const a = anterior || {};
    const n = nuevo || {};
    const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(n)])).sort();

    return (
        <div style={{ overflow: 'auto' }}>
            <table className="standard-table" style={{ fontSize: '0.8rem', marginBottom: 0 }}>
                <thead>
                    <tr>
                        <th style={{ width: '30%' }}>Campo</th>
                        <th style={{ width: '35%' }}>Anterior</th>
                        <th style={{ width: '35%' }}>Nuevo</th>
                    </tr>
                </thead>
                <tbody>
                    {keys.length === 0 ? (
                        <tr><td colSpan="3" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Sin datos</td></tr>
                    ) : keys.map(k => {
                        const oldVal = a[k];
                        const newVal = n[k];
                        const hasOld = Object.prototype.hasOwnProperty.call(a, k);
                        const hasNew = Object.prototype.hasOwnProperty.call(n, k);
                        const changed = !isEqual(oldVal, newVal);
                        const added = !hasOld && hasNew;
                        const removed = hasOld && !hasNew;

                        let rowBg = 'transparent';
                        let textColor = 'inherit';
                        if (added || removed || changed) {
                            rowBg = '#fee2e2'; // rojo claro
                            textColor = '#991b1b';
                        }

                        return (
                            <tr key={k} style={{ background: rowBg, color: textColor }}>
                                <td style={{ fontWeight: 600 }}>{k}</td>
                                <td style={{ textDecoration: removed ? 'line-through' : 'none' }}>{hasOld ? formatValue(oldVal) : '—'}</td>
                                <td style={{ fontWeight: changed || added ? 600 : 'normal' }}>{hasNew ? formatValue(newVal) : '—'}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default function AuditoriaManager({ usuario }) {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [entidad, setEntidad] = useState('');
    const [accion, setAccion] = useState('');
    const [usuarioFiltro, setUsuarioFiltro] = useState('');
    const [limit, setLimit] = useState(100);
    const [seleccionado, setSeleccionado] = useState(null);

    useEffect(() => {
        fetchLogs();
    }, [entidad, accion, usuarioFiltro, limit]);

    const fetchWithAuth = async (url) => {
        const token = localStorage.getItem('authToken');
        return fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
    };

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit });
            if (entidad) params.append('entidad', entidad);
            if (accion) params.append('accion', accion);
            if (usuarioFiltro) params.append('usuario', usuarioFiltro);
            const res = await fetchWithAuth(`${API_BASE_URL}/api/auditoria-modificaciones?${params.toString()}`);
            if (res.ok) {
                const data = await res.json();
                setLogs(data);
                setError('');
            } else {
                const data = await res.json();
                setError(data.error || 'Error al cargar auditoría');
                setLogs([]);
            }
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="manager-container">
            <h2>Auditoría de Modificaciones</h2>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'end' }}>
                <div className="form-group" style={{ marginBottom: 0, minWidth: '140px' }}>
                    <label>Acción</label>
                    <select value={accion} onChange={e => setAccion(e.target.value)} style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                        {ACCIONES.map(o => (
                            <option key={o} value={o}>{o || 'Todas'}</option>
                        ))}
                    </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0, minWidth: '180px' }}>
                    <label>Módulo / Entidad</label>
                    <input
                        type="text"
                        value={entidad}
                        onChange={e => setEntidad(e.target.value)}
                        placeholder="Ej: vehiculos"
                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                    />
                </div>
                <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: '200px' }}>
                    <label>Usuario</label>
                    <input
                        type="text"
                        value={usuarioFiltro}
                        onChange={e => setUsuarioFiltro(e.target.value)}
                        placeholder="Nombre o correo"
                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', width: '100%' }}
                    />
                </div>
                <div className="form-group" style={{ marginBottom: 0, minWidth: '100px' }}>
                    <label>Límite</label>
                    <select value={limit} onChange={e => setLimit(Number(e.target.value))} style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={500}>500</option>
                        <option value={1000}>1000</option>
                    </select>
                </div>
                <button onClick={() => { setAccion(''); setEntidad(''); setUsuarioFiltro(''); setLimit(100); }} className="btn-secondary">
                    Limpiar
                </button>
            </div>

            {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

            {loading ? <p>Cargando auditoría...</p> : (
                <div className="table-responsive">
                    <table className="standard-table" style={{ fontSize: '0.85rem' }}>
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Usuario</th>
                                <th>Acción</th>
                                <th>Módulo</th>
                                <th>ID Registro</th>
                                <th>IP</th>
                                <th style={{ width: '100px' }}>Detalle</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.length > 0 ? logs.map(log => (
                                <tr key={log.id}>
                                    <td data-label="Fecha">{formatFecha(log.fecha)}</td>
                                    <td data-label="Usuario">
                                        <div style={{ fontWeight: 600 }}>{log.usuario_nombre || '---'}</div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{log.usuario_username || '---'}</div>
                                    </td>
                                    <td data-label="Acción">
                                        <span style={{
                                            padding: '3px 8px',
                                            borderRadius: '12px',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            background: log.accion === 'Agrega' ? '#dcfce7' : log.accion === 'Modifica' ? '#fef3c7' : log.accion === 'Elimina' ? '#fee2e2' : '#eff6ff',
                                            color: log.accion === 'Agrega' ? '#166534' : log.accion === 'Modifica' ? '#92400e' : log.accion === 'Elimina' ? '#991b1b' : '#1e40af'
                                        }}>{log.accion}</span>
                                    </td>
                                    <td data-label="Módulo">{nombreModulo(log.tipo_dato)}</td>
                                    <td data-label="ID Registro" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{log.id_registro || '---'}</td>
                                    <td data-label="IP" style={{ fontSize: '0.75rem' }}>{log.ip || '---'}</td>
                                    <td data-label="Detalle" style={{ width: '100px' }}>
                                        <button onClick={() => setSeleccionado(log)} className="btn-action btn-details" style={{ padding: '4px 10px', fontSize: '0.8rem' }}>
                                            Ver
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)' }}>No hay registros de auditoría.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {seleccionado && (
                <div className="modal" onClick={() => setSeleccionado(null)}>
                    <div className="standard-form" style={{ width: '100%', maxWidth: '720px', maxHeight: '85vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ marginBottom: '8px', color: 'var(--sidebar-bg)' }}>Detalle de auditoría</h3>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                            <div><strong>Usuario:</strong><br/>{seleccionado.usuario_nombre || '---'}<br/><span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{seleccionado.usuario_username || '---'}</span></div>
                            <div><strong>Acción:</strong><br/>{seleccionado.accion}</div>
                            <div><strong>Módulo:</strong><br/>{nombreModulo(seleccionado.tipo_dato)}</div>
                            <div><strong>Fecha:</strong><br/>{formatFecha(seleccionado.fecha)}</div>
                            <div style={{ gridColumn: '1 / -1' }}><strong>Entidad:</strong><br/><code style={{ fontSize: '0.8rem' }}>{seleccionado.tipo_dato}</code></div>
                            <div style={{ gridColumn: '1 / -1' }}><strong>ID Registro:</strong><br/>{seleccionado.id_registro || '---'}</div>
                            <div style={{ gridColumn: '1 / -1' }}><strong>IP:</strong><br/>{seleccionado.ip || '---'}</div>
                        </div>

                        {seleccionado.valores_anteriores && seleccionado.valores_nuevos ? (
                            <div style={{ marginBottom: '16px' }}>
                                <h4 style={{ marginBottom: '6px' }}>Comparación de cambios</h4>
                                {renderDiff(seleccionado.valores_anteriores, seleccionado.valores_nuevos)}
                            </div>
                        ) : (
                            <>
                                {seleccionado.valores_anteriores && (
                                    <div style={{ marginBottom: '16px' }}>
                                        <h4 style={{ marginBottom: '6px' }}>Valores anteriores</h4>
                                        {jsonViewer(seleccionado.valores_anteriores)}
                                    </div>
                                )}
                                {seleccionado.valores_nuevos && (
                                    <div style={{ marginBottom: '16px' }}>
                                        <h4 style={{ marginBottom: '6px' }}>Valores nuevos / Registro</h4>
                                        {seleccionado.valores_nuevos && typeof seleccionado.valores_nuevos === 'object' && !Array.isArray(seleccionado.valores_nuevos) ? (
                                            renderDiff({}, seleccionado.valores_nuevos)
                                        ) : (
                                            jsonViewer(seleccionado.valores_nuevos)
                                        )}
                                    </div>
                                )}
                            </>
                        )}

                        {seleccionado.valores_completos && !seleccionado.valores_nuevos && (
                            <div style={{ marginBottom: '16px' }}>
                                <h4 style={{ marginBottom: '6px' }}>Registro eliminado</h4>
                                {renderDiff(seleccionado.valores_completos, {})}
                            </div>
                        )}

                        {seleccionado.detalle && (
                            <div style={{ marginBottom: '16px' }}>
                                <h4 style={{ marginBottom: '6px' }}>Detalle adicional</h4>
                                {jsonViewer(typeof seleccionado.detalle === 'string' ? JSON.parse(seleccionado.detalle) : seleccionado.detalle)}
                            </div>
                        )}

                        <div className="form-actions">
                            <button onClick={() => setSeleccionado(null)} className="btn-secondary">Cerrar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
